import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight, Code2, Copy, Settings } from 'lucide-react';
import { Modal } from './ui/Modal';
import CodeEditor from './ui/CodeEditor';
import {
  buildRequestParts, CODE_GENERATORS,
  DEFAULT_CURL_OPTIONS,
  DEFAULT_PYTHON_OPTIONS,
  DEFAULT_TS_OPTIONS,
  DEFAULT_GO_OPTIONS,
  DEFAULT_JAVA_OPTIONS,
  DEFAULT_CSHARP_OPTIONS,
  DEFAULT_PHP_OPTIONS,
  DEFAULT_RUBY_OPTIONS,
  DEFAULT_SWIFT_OPTIONS,
} from '../utils/codeGenerators';

const LANG_META = {
  curl:       { library: 'Shell' },
  python:     { library: 'Requests' },
  typescript: { library: 'Fetch' },
  go:         { library: 'net/http' },
  java:       { library: 'HttpClient' },
  csharp:     { library: 'HttpClient' },
  php:        { library: 'cURL' },
  ruby:       { library: 'Net::HTTP' },
  swift:      { library: 'URLSession' },
};

const EDITOR_LANGUAGES = {
  curl: 'shell', python: 'python', typescript: 'typescript',
  go: 'go', java: 'java', csharp: 'csharp',
  php: 'php', ruby: 'ruby', swift: 'swift',
};

// Settings schema — each field descriptor drives the generic renderer
const LANG_SETTINGS = {
  curl: [
    { key: 'multiline',       type: 'toggle', label: 'Multiline',         desc: 'Split the command across multiple lines using \\' },
    { key: 'shortFlags',      type: 'toggle', label: 'Short flags',        desc: 'Use compact flags: -H, -d, -X instead of --header, --data, --request' },
    { key: 'followRedirects', type: 'toggle', label: 'Follow redirects',   desc: 'Automatically follow HTTP redirects (-L / --location)' },
    { key: 'silent',          type: 'toggle', label: 'Silent mode',        desc: 'Hide progress meter and error messages (-s / --silent)' },
    { key: 'quoteStyle',      type: 'select', label: 'Quote style',        desc: "Single quotes work on Unix/macOS; use double quotes for Windows cmd.exe",
      options: [{ value: 'single', label: "Single '" }, { value: 'double', label: 'Double "' }] },
    { key: 'timeout',         type: 'number', label: 'Timeout',            desc: 'Max time in seconds (--max-time). Set to 0 for no limit', unit: 'sec', min: 0 },
  ],
  python: [
    { key: 'responseFormat',  type: 'select', label: 'Response format',    desc: 'How to read and print the response body',
      options: [{ value: 'text', label: 'Text' }, { value: 'json', label: 'JSON' }] },
    { key: 'followRedirects', type: 'toggle', label: 'Follow redirects',   desc: 'Allow the library to follow 3xx redirects automatically' },
    { key: 'verifySsl',       type: 'toggle', label: 'Verify SSL',         desc: 'Validate the server SSL certificate (disable for self-signed certs)' },
    { key: 'timeout',         type: 'number', label: 'Timeout',            desc: 'Request timeout in seconds. Set to 0 for no limit', unit: 'sec', min: 0 },
  ],
  typescript: [
    { key: 'responseFormat',  type: 'select', label: 'Response format',    desc: 'How to parse and log the response body',
      options: [{ value: 'json', label: 'JSON' }, { value: 'text', label: 'Text' }, { value: 'none', label: 'None' }] },
    { key: 'credentials',     type: 'select', label: 'Credentials',        desc: 'Controls whether cookies and auth headers are sent cross-origin',
      options: [{ value: 'omit', label: 'Omit' }, { value: 'same-origin', label: 'Same-origin' }, { value: 'include', label: 'Include' }] },
  ],
  go: [
    { key: 'timeout', type: 'number', label: 'Timeout', desc: 'HTTP client timeout in seconds. Set to 0 for no limit', unit: 'sec', min: 0 },
  ],
  java: [
    { key: 'async',   type: 'toggle', label: 'Async (sendAsync)',  desc: 'Use sendAsync() with a thenApply() callback instead of the blocking send()' },
    { key: 'timeout', type: 'number', label: 'Timeout',            desc: 'Connect + request timeout in seconds. Set to 0 for no limit', unit: 'sec', min: 0 },
  ],
  csharp: [
    { key: 'topLevel', type: 'toggle', label: 'Top-level statements', desc: 'Emit .NET 6+ top-level code without the class Program / static void Main wrapper' },
    { key: 'timeout',  type: 'number', label: 'Timeout',              desc: 'HttpClient.Timeout in seconds. Set to 0 for no limit', unit: 'sec', min: 0 },
  ],
  php: [
    { key: 'followRedirects', type: 'toggle', label: 'Follow redirects', desc: 'Set CURLOPT_FOLLOWLOCATION to automatically follow 3xx redirects' },
    { key: 'verifySsl',       type: 'toggle', label: 'Verify SSL',       desc: 'Validate server SSL certificate (CURLOPT_SSL_VERIFYPEER)' },
    { key: 'timeout',         type: 'number', label: 'Timeout',          desc: 'CURLOPT_TIMEOUT in seconds. Set to 0 for no limit', unit: 'sec', min: 0 },
  ],
  ruby: [
    { key: 'verifySsl', type: 'toggle', label: 'Verify SSL', desc: 'Validate server SSL certificate (set to off for self-signed certs)' },
    { key: 'timeout',   type: 'number', label: 'Timeout',    desc: 'http.read_timeout in seconds. Set to 0 for no limit', unit: 'sec', min: 0 },
  ],
  swift: [
    { key: 'responseFormat', type: 'select', label: 'Response format', desc: 'How to decode and print the response data',
      options: [{ value: 'text', label: 'String' }, { value: 'json', label: 'JSON' }] },
    { key: 'timeout', type: 'number', label: 'Timeout', desc: 'URLRequest timeoutInterval in seconds', unit: 'sec', min: 1 },
  ],
};

const DEFAULT_OPTIONS = {
  curl:       DEFAULT_CURL_OPTIONS,
  python:     DEFAULT_PYTHON_OPTIONS,
  typescript: DEFAULT_TS_OPTIONS,
  go:         DEFAULT_GO_OPTIONS,
  java:       DEFAULT_JAVA_OPTIONS,
  csharp:     DEFAULT_CSHARP_OPTIONS,
  php:        DEFAULT_PHP_OPTIONS,
  ruby:       DEFAULT_RUBY_OPTIONS,
  swift:      DEFAULT_SWIFT_OPTIONS,
};

function methodBadgeClass(method) {
  switch ((method || '').toUpperCase()) {
    case 'GET':    return 'code-gen-badge code-gen-badge--get';
    case 'POST':   return 'code-gen-badge code-gen-badge--post';
    case 'PUT':    return 'code-gen-badge code-gen-badge--put';
    case 'PATCH':  return 'code-gen-badge code-gen-badge--patch';
    case 'DELETE': return 'code-gen-badge code-gen-badge--delete';
    case 'HEAD':   return 'code-gen-badge code-gen-badge--head';
    default:       return 'code-gen-badge code-gen-badge--default';
  }
}

function Toggle({ on, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className={`settings-toggle${on ? ' settings-toggle--on' : ''}`}
    >
      <span className="settings-toggle-thumb" />
    </button>
  );
}

function LangSettings({ langId, options, onChange, revealSecrets, onRevealSecretsChange }) {
  const fields = LANG_SETTINGS[langId] || [];
  const label = CODE_GENERATORS.find((g) => g.id === langId)?.label ?? langId;

  const set = (key) => (val) => onChange({ ...options, [key]: val });

  return (
    <div className="code-gen-settings">
      <div className="code-gen-settings__title">Request</div>
      <div className="code-gen-settings__body">
        <div className="settings-row">
          <div className="code-gen-settings__row-text">
            <div className="settings-row-label">Include auth</div>
            <div className="settings-row-desc">
              Fill in secret variables instead of redacting them, so the snippet runs as-is.
              Leave off when sharing it — the credentials travel with the text.
            </div>
          </div>
          <Toggle on={!!revealSecrets} onChange={onRevealSecretsChange} />
        </div>
      </div>
      {fields.length > 0 && <div className="code-gen-settings__title">{label} options</div>}
      <div className="code-gen-settings__body">
        {fields.map((field) => (
          <div key={field.key} className="settings-row">
            <div className="code-gen-settings__row-text">
              <div className="settings-row-label">{field.label}</div>
              <div className="settings-row-desc">{field.desc}</div>
            </div>
            {field.type === 'toggle' && (
              <Toggle on={!!options[field.key]} onChange={set(field.key)} />
            )}
            {field.type === 'select' && (
              <select
                className="settings-select-native"
                value={options[field.key]}
                onChange={(e) => set(field.key)(e.target.value)}
                style={{ width: 110 }}
              >
                {field.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            )}
            {field.type === 'number' && (
              <div className="settings-input-group" style={{ width: 80 }}>
                <input
                  type="number"
                  min={field.min ?? 0}
                  className="settings-number-input"
                  style={{ width: 48 }}
                  value={options[field.key]}
                  onChange={(e) => set(field.key)(Math.max(field.min ?? 0, Number(e.target.value) || 0))}
                />
                {field.unit && <span className="settings-unit-label">{field.unit}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CodeGeneratorModal({ isOpen, onClose, request, collectionAuth, folderAuthChain, variables }) {
  const [selectedLang, setSelectedLang] = useState('curl');
  const [copied, setCopied] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [langOptions, setLangOptions] = useState(() =>
    Object.fromEntries(Object.entries(DEFAULT_OPTIONS).map(([k, v]) => [k, { ...v }]))
  );
  const copyTimerRef = useRef(null);

  useEffect(() => {
    if (isOpen) { setSelectedLang('curl'); setShowSettings(false); }
  }, [isOpen]);

  useEffect(() => {
    if (!LANG_SETTINGS[selectedLang]?.length) setShowSettings(false);
  }, [selectedLang]);

  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  // Deliberately NOT part of langOptions: it is a property of the request, not
  // of the language, and it must be reachable for languages that have no other
  // settings. Off by default — see buildRedactingSubstitute for why.
  const [revealSecrets, setRevealSecrets] = useState(false);

  const parts = useMemo(() => {
    if (!request) return null;
    try {
      return buildRequestParts(request, collectionAuth, variables ?? null, { revealSecrets, folderAuthChain });
    } catch {
      return null;
    }
  }, [request, collectionAuth, folderAuthChain, variables, revealSecrets]);

  const code = useMemo(() => {
    if (!parts) return '';
    const gen = CODE_GENERATORS.find((g) => g.id === selectedLang);
    if (!gen) return '';
    try { return gen.fn(parts, langOptions[selectedLang] ?? {}); } catch { return ''; }
  }, [parts, selectedLang, langOptions]);

  const handleCopy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleOptionsChange = (newOpts) => {
    setLangOptions((prev) => ({ ...prev, [selectedLang]: newOpts }));
  };

  if (!request) return null;

  const method = (request.method || 'GET').toUpperCase();
  const selectedGen = CODE_GENERATORS.find((g) => g.id === selectedLang);
  // Always true now: even a language with no options of its own has this one,
  // so the settings affordance never disappears.
  const hasSettings = true;

  const subtitleNode = (
    <span className="code-gen-subtitle">
      <span className={methodBadgeClass(method)}>{method}</span>
      <span className="code-gen-url">{request.url || ''}</span>
    </span>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate code"
      subtitle={subtitleNode}
      size="xl"
      contentClassName="code-gen-modal"
      customIcon={
        <div className="code-gen-modal-icon">
          <Code2 size={20} style={{ color: 'var(--accent)' }} />
        </div>
      }
    >
      <div className="code-gen-wrap" style={{ height: '560px' }}>
        {/* Left panel — language list */}
        <div className="code-gen-sidebar">
          {CODE_GENERATORS.map((gen) => {
            const active = selectedLang === gen.id;
            return (
              <button
                key={gen.id}
                type="button"
                onClick={() => setSelectedLang(gen.id)}
                className={`code-gen-item${active ? ' code-gen-item--active' : ''}`}
              >
                <span className="code-gen-item__name">{gen.label}</span>
                <span className="code-gen-item__sub">{LANG_META[gen.id]?.library}</span>
              </button>
            );
          })}
        </div>

        {/* Right panel — toolbar + editor/settings */}
        <div className="code-gen-main">
          <div className="code-gen-toolbar">
            <div className="code-gen-toolbar__label">
              <ChevronRight size={13} />
              <span>{selectedGen?.label}</span>
            </div>
            <div className="code-gen-toolbar__actions">
              {hasSettings && (
                <button
                  type="button"
                  onClick={() => setShowSettings((v) => !v)}
                  className={`code-gen-icon-btn${showSettings ? ' code-gen-icon-btn--active' : ''}`}
                  title="Options"
                  aria-label="Options"
                >
                  <Settings size={14} />
                </button>
              )}
              <button
                type="button"
                onClick={handleCopy}
                className={`code-gen-icon-btn${copied ? ' code-gen-icon-btn--copied' : ''}`}
                title={copied ? 'Copied!' : 'Copy'}
                aria-label={copied ? 'Copied!' : 'Copy'}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          {showSettings ? (
            <LangSettings
              langId={selectedLang}
              revealSecrets={revealSecrets}
              onRevealSecretsChange={setRevealSecrets}
              options={langOptions[selectedLang] ?? {}}
              onChange={handleOptionsChange}
            />
          ) : (
            <div className="code-gen-editor-wrap">
              <CodeEditor
                value={code}
                onChange={() => {}}
                language={EDITOR_LANGUAGES[selectedLang] || 'text'}
                readOnly
                hideChrome
                height="100%"
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
